import { useCallback, useEffect, useMemo, type ReactNode } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";

import { SplashScreen } from "@/shell/SplashScreen";
import { AuthErrorPage } from "./AuthErrorPage";
import {
  clearMissingOrganizationRetry,
  consumeMissingOrganizationRetry,
  isMissingOrganizationError,
} from "./authOrganization";
import {
  buildLoginWithRedirectOptions,
  readInvitationParams,
} from "./invitationParams";
import { authReturnTo, nativeOpenUrl } from "./nativeAuth";
import { signalAuthLogout } from "./sessionSync";

interface Props {
  children: ReactNode;
}

/**
 * Auth0 error codes that must not be retried automatically — retrying
 * them would cause an infinite logout → login loop.
 */
const NON_RECOVERABLE_ERRORS = new Set([
  "access_denied",
  "invalid_request",
  "unauthorized",
  "consent_required",
  "interaction_required",
]);

function useUrlAuthError(): {
  errorCode: string | null;
  errorDescription: string | null;
} {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      errorCode: params.get("error"),
      errorDescription: params.get("error_description"),
    };
  }, []);
}

function stripAuthErrorParams(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete("error");
  url.searchParams.delete("error_description");
  url.searchParams.delete("state");
  const next = url.pathname + url.search;
  window.history.replaceState({}, "", next);
  return next;
}

/**
 * Guard that redirects unauthenticated users to Auth0 Universal Login.
 * Organization selection is handled by Auth0 (org prompt), not in-app UI.
 */
export function ProtectedRoute({ children }: Props) {
  const {
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    logout,
  } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const urlError = useUrlAuthError();

  const effectiveErrorCode =
    (error as { error?: string } | undefined)?.error ||
    urlError.errorCode ||
    (error ? "unknown" : null);

  const effectiveErrorDescription =
    error?.message || urlError.errorDescription || null;

  const missingOrganization = isMissingOrganizationError(
    effectiveErrorCode,
    effectiveErrorDescription,
  );

  const isNonRecoverable =
    effectiveErrorCode !== null &&
    NON_RECOVERABLE_ERRORS.has(effectiveErrorCode) &&
    !missingOrganization;

  useEffect(() => {
    if (isAuthenticated) clearMissingOrganizationRetry();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!missingOrganization) return;
    if (!consumeMissingOrganizationRetry()) return;
    const cleaned = stripAuthErrorParams();
    void loginWithRedirect(
      buildLoginWithRedirectOptions(readInvitationParams(cleaned), cleaned),
    );
  }, [missingOrganization, loginWithRedirect]);

  useEffect(() => {
    if (error && !isNonRecoverable && !missingOrganization) {
      console.error("Auth0 recoverable error, clearing session:", error.message);
      signalAuthLogout();
      logout({
        logoutParams: { returnTo: authReturnTo() },
        openUrl: nativeOpenUrl(),
      });
    }
  }, [error, isNonRecoverable, missingOrganization, logout]);

  useEffect(() => {
    if (isLoading || isAuthenticated || effectiveErrorCode) return;
    const search = location.search;
    const params = readInvitationParams(search);
    if (params && location.pathname !== "/login") {
      navigate(`/login${search}`, { replace: true });
      return;
    }
    void loginWithRedirect(
      buildLoginWithRedirectOptions(
        params,
        `${location.pathname}${location.search}` || "/",
      ),
    );
  }, [
    isLoading,
    isAuthenticated,
    effectiveErrorCode,
    location.pathname,
    location.search,
    loginWithRedirect,
    navigate,
  ]);

  const handleRetry = useCallback(() => {
    const cleaned = stripAuthErrorParams();
    void loginWithRedirect(
      buildLoginWithRedirectOptions(readInvitationParams(cleaned), cleaned),
    );
  }, [loginWithRedirect]);

  const handleLogout = useCallback(() => {
    stripAuthErrorParams();
    signalAuthLogout();
    logout({
      logoutParams: { returnTo: authReturnTo() },
      openUrl: nativeOpenUrl(),
    });
  }, [logout]);

  if (isLoading || missingOrganization) {
    return <SplashScreen />;
  }

  if (isNonRecoverable || urlError.errorCode) {
    return (
      <AuthErrorPage
        errorCode={effectiveErrorCode ?? urlError.errorCode ?? undefined}
        errorDescription={
          effectiveErrorDescription ?? urlError.errorDescription
        }
        onRetry={handleRetry}
        onLogout={handleLogout}
      />
    );
  }

  if (error || !isAuthenticated) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
